/**
 * The design, as values.
 *
 * Every number and colour here is read out of `maestro-handoff/design/*.html`,
 * which is the design. They are in one file so a screen never invents a shade
 * or a radius — the mockups are consistent to the hex digit, and the fastest
 * way to look unlike them is for each screen to approximate.
 *
 * CLAUDE.md names five of these out loud: ground, ink, accent blue, good teal,
 * stale orange, and a 44px minimum on anything you touch.
 */

export const colour = {
  /** The page. */
  ground: '#f4f3ef',
  /** Cards and anything raised off the page. */
  surface: '#ffffff',
  /** A surface sitting on a surface, like a filled table cell. */
  surfaceSunk: '#eceae3',

  /** Body text. */
  ink: '#1b1c1e',
  /** Secondary text: descriptions, reasons, the second line of a row. */
  inkSoft: '#3d3c38',
  /** Labels above a control, and small print. */
  inkMuted: '#5c5a53',
  /** Text that is present but not being read. */
  inkFaint: '#8a8378',

  /** The line round a card or an input. */
  edge: '#cfccc2',
  /** A divider inside a card, lighter than its own edge. */
  edgeSoft: '#e7e4da',
  /** A heavier edge, for a cell that needs to read as filled. */
  edgeStrong: '#8a8378',
  /** The shade of an empty heatmap cell. */
  cellEmpty: '#dedbd0',

  /** Actions. */
  accent: '#2f5bea',
  /** A link in running text: darker, for contrast on the ground. */
  link: '#2447b8',
  /** A selected chip. */
  accentWash: '#eef2ff',
  accentInk: '#1d3a9e',

  /** Good, and current. */
  good: '#0f766e',
  goodWash: '#e6f2f0',
  goodDeep: '#0b5b55',

  /** Stale, failed, and anything needing a person. */
  stale: '#b45309',
  staleWash: '#fdf0e2',
  staleInk: '#8f3f05',

  /** The menu bar, which is dark on every screen. */
  barBg: '#1b1c1e',
  barRaised: '#2b2c30',
  barEdge: '#4a4b50',
  barInk: '#ffffff',

  /** The app icon's gradient and its one teal dot. */
  iconFrom: '#3f6dff',
  iconTo: '#1c34a0',
  iconDot: '#2dd4bf'
} as const;

/** Every interactive thing is at least this tall (CLAUDE.md). */
export const TOUCH = 44;

export const radius = {
  /** Buttons, inputs, selects. */
  control: 8,
  /** Cards and panels. */
  card: 10,
  /** Inside a menu, where the item is already inside a rounded panel. */
  item: 6,
  /** Chips and pills. */
  pill: 999
} as const;

export const space = {
  /** Between sections of a page. */
  section: 24,
  /** Inside a card. */
  card: 20,
  /** Between controls in a row. */
  control: 12,
  /** Between a label and its control. */
  label: 2,
  tight: 8,
  hair: 4
} as const;

/** The type scale, as `font` shorthand values. */
export const text = {
  /** The small caps line above a page title. */
  eyebrow: { font: "600 13px/18px 'IBM Plex Sans', system-ui, sans-serif", letterSpacing: '0.08em' },
  h1: { font: "600 32px/40px 'IBM Plex Sans', system-ui, sans-serif" },
  h2: { font: "600 20px/28px 'IBM Plex Sans', system-ui, sans-serif" },
  h3: { font: "600 17px/24px 'IBM Plex Sans', system-ui, sans-serif" },
  /** A big number on a summary tile. */
  figure: { font: "600 28px/32px 'IBM Plex Sans', system-ui, sans-serif" },
  body: { font: "400 15px/22px 'IBM Plex Sans', system-ui, sans-serif" },
  bodyStrong: { font: "600 15px/22px 'IBM Plex Sans', system-ui, sans-serif" },
  small: { font: "400 14px/20px 'IBM Plex Sans', system-ui, sans-serif" },
  smallStrong: { font: "500 14px/20px 'IBM Plex Sans', system-ui, sans-serif" },
  label: { font: "400 12px/16px 'IBM Plex Sans', system-ui, sans-serif" },
  note: { font: "400 13px/18px 'IBM Plex Sans', system-ui, sans-serif" },
  /** Paths, ids, timecode — anything that has to line up or be copied. */
  mono: { font: "400 13px/18px 'IBM Plex Mono', ui-monospace, monospace" },
  monoStrong: { font: "500 13px/18px 'IBM Plex Mono', ui-monospace, monospace" }
} as const;

export const shadow = {
  /** A menu or dialog floating over the page. */
  float: '0 12px 32px rgba(27, 28, 30, 0.22)',
  /** A dialog, which sits over more. */
  dialog: '0 24px 64px rgba(27, 28, 30, 0.28)'
} as const;

/**
 * The nine statuses, each with a word as well as a colour, because nothing in
 * Maestro relies on colour alone (docs/04, cross-cutting). The words match
 * `src/content/help.ts` on the server, which is where the legend is defined —
 * these are the colours for them.
 */
export const statusColour: Record<string, { fg: string; bg: string; edge: string }> = {
  good: { fg: colour.goodDeep, bg: colour.goodWash, edge: colour.good },
  stale: { fg: colour.staleInk, bg: colour.staleWash, edge: colour.stale },
  accepted_stale: { fg: colour.staleInk, bg: colour.staleWash, edge: colour.stale },
  candidate: { fg: colour.inkSoft, bg: colour.surfaceSunk, edge: colour.edge },
  superseded: { fg: colour.inkFaint, bg: colour.surfaceSunk, edge: colour.edge },
  bad: { fg: colour.staleInk, bg: colour.staleWash, edge: colour.stale },
  blocked: { fg: colour.accentInk, bg: colour.accentWash, edge: colour.accent },
  notmade: { fg: colour.inkMuted, bg: colour.surface, edge: colour.edge },
  failed: { fg: colour.staleInk, bg: colour.staleWash, edge: colour.stale },
  running: { fg: colour.accentInk, bg: colour.accentWash, edge: colour.accent },
  queued: { fg: colour.inkSoft, bg: colour.surfaceSunk, edge: colour.edge },
  current: { fg: colour.goodDeep, bg: colour.goodWash, edge: colour.good },
  missing: { fg: colour.inkMuted, bg: colour.surface, edge: colour.edge },
  done: { fg: colour.goodDeep, bg: colour.goodWash, edge: colour.good },
  cancelled: { fg: colour.inkFaint, bg: colour.surfaceSunk, edge: colour.edge },
  ready: { fg: colour.goodDeep, bg: colour.goodWash, edge: colour.good },
  planned: { fg: colour.accentInk, bg: colour.accentWash, edge: colour.accent }
};

/** Plain words for a status, so no screen shows a bare key. */
export const statusWord: Record<string, string> = {
  good: 'Good',
  stale: 'Stale',
  accepted_stale: 'Stale, kept as it is',
  candidate: 'Candidate',
  superseded: 'Superseded',
  bad: 'Bad',
  blocked: 'Blocked',
  notmade: 'Not made',
  failed: 'Failed',
  running: 'Running',
  queued: 'Queued',
  current: 'Current',
  missing: 'Not made',
  done: 'Done',
  cancelled: 'Cancelled',
  ready: 'Current',
  planned: 'Not created yet'
};

/**
 * The heatmap's shade for how far along a pass is: light at the start, dark at
 * the end, so a column reads at a glance from across the room. Index -1 is
 * nothing made yet.
 */
export function progressShade(index: number, steps: number): string {
  if (index < 0) return colour.cellEmpty;
  const share = steps <= 1 ? 1 : Math.min(1, (index + 1) / steps);
  // From the ground's warm grey to the good teal, which is where a finished
  // pass should land.
  const from = [222, 219, 208];
  const to = [15, 118, 110];
  const mix = from.map((f, i) => Math.round(f + (to[i]! - f) * share));
  return `rgb(${mix.join(', ')})`;
}
